import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  FiClock, FiLoader, FiShuffle, FiStar, FiTrendingUp,
} from 'react-icons/fi';
import { cCategories, cFeed, cShops, cUsers, fullUrl } from '../../api';
import { readFeed, saveFeed } from '../../utils/feedCache';
import { useCommunity } from '../../contexts/CommunityContext';
import { useLanguage } from '../../i18n';
import CommunitySidebar from '../../components/community/CommunitySidebar';
import ProductRail from '../../components/community/ProductRail';
import ShopsRail from '../../components/community/ShopsRail';
import FollowButton from '../../components/community/FollowButton';
import PostCard from '../../components/community/PostCard';
import PostComposer from '../../components/community/PostComposer';
import StoriesBar from '../../components/community/StoriesBar';
import UserAvatar from '../../components/community/UserAvatar';
import { BLUE } from '../../components/community/brand';

/**
 * The community home: articles feed (Latest / Popular / Following) + sidebar.
 *
 * The list survives opening an article: `utils/feedCache` hands back the posts,
 * the page number, the filters and the reading position, so “Back to feed” shows
 * exactly what the reader was looking at (and they can keep scrolling) instead
 * of reloading page 1.
 */
export default function CommunityFeed() {
  const { t, lang } = useLanguage();
  const { user } = useCommunity();
  const [params] = useSearchParams();
  const q = params.get('q') || '';
  const categoryId = params.get('category_id') || '';
  const writing = params.get('write') === '1';

  const PAGE_SIZE = 10;              // 10 articles per load
  const AUTO_BEFORE = 5;             // …load 10 more when only 5 are left to read

  // ── What the reader had before opening an article ─────────────────────────
  const cache = readFeed();
  const userId = user?.id || 0;
  const boot = useRef({
    sort: cache.sort || 'random',
    scope: cache.scope || 'all',
    seed: cache.seed || Math.floor(1000 + Math.random() * 900000),
  });
  const bootKey = `${q}|${categoryId}|${boot.current.sort}|${boot.current.scope}|${userId}`;
  // Only reuse the cache when it belongs to exactly the same filters.
  const restored = useRef(bootKey === cache.key && (cache.posts || []).length > 0);
  const cached = restored.current ? cache : null;

  const [sort, setSort] = useState(boot.current.sort);   // 🎲 mixed up on purpose
  const [scope, setScope] = useState(boot.current.scope);
  const [page, setPage] = useState(cached?.page || 1);
  const [posts, setPosts] = useState(cached?.posts || []);
  const [pages, setPages] = useState(cached?.pages || 1);
  const [total, setTotal] = useState(cached?.total || 0);
  // “ទាំងអស់” (All) row: the global published count, whatever the filter is.
  const [allTotal, setAllTotal] = useState(cached?.allTotal || 0);
  const [loading, setLoading] = useState(!cached);
  // One seed per visit → the "Random" order stays stable while paging (no repeats).
  const seedRef = useRef(boot.current.seed);
  const sentinel = useRef(null);
  const loadingRef = useRef(false);
  const [cats, setCats] = useState([]);
  const [people, setPeople] = useState([]);
  const [shops, setShops] = useState([]);
  const [tick, setTick] = useState(0);
  const paramsKey = `${q}|${categoryId}|${sort}|${scope}|${userId}`;

  useEffect(() => {
    cCategories().then((rows) => setCats(Array.isArray(rows) ? rows : [])).catch(() => {});
    cUsers({ limit: 5 }).then((res) => setPeople(res?.items || [])).catch(() => {});
    cShops({ limit: 4 }).then((res) => setShops(res?.items || [])).catch(() => {});
  }, []);

  // A new search / category / tab starts from the first page — but the very
  // first render keeps the page of the restored list, so “Back” does not reset.
  const lastFilters = useRef(paramsKey);
  useEffect(() => {
    if (lastFilters.current === paramsKey) return;
    lastFilters.current = paramsKey;
    setPage(1);
  }, [paramsKey]);

  // Keep the cache up to date → the next visit (or Back) gets this exact list.
  useEffect(() => {
    saveFeed({ key: paramsKey, posts, page, pages, total, allTotal, sort, scope, seed: seedRef.current });
  }, [paramsKey, posts, page, pages, total, allTotal, sort, scope]);

  // Back from an article → put the reader back where they were.
  useEffect(() => {
    if (!restored.current) return undefined;
    const y = cache.scrollY || 0;
    if (!y) return undefined;
    const to = () => window.scrollTo({ top: y, behavior: 'auto' });
    to();
    const a = setTimeout(to, 150);      // images/layout settle a moment later
    const b = setTimeout(to, 450);
    return () => { clearTimeout(a); clearTimeout(b); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remember the reading position (memory only — flushed when the tab hides).
  useEffect(() => {
    const onScroll = () => saveFeed({ scrollY: window.scrollY }, false);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Fetch only when something really changed. The first render of a restored
  // list deliberately does nothing — those articles are already on screen (this
  // also absorbs React StrictMode's double-invoked effects in development).
  const lastRun = useRef({
    key: restored.current ? paramsKey : '',
    page: cached?.page || 0,
    tick: restored.current ? 0 : -1,
    userId: restored.current ? userId : -1,
  });
  useEffect(() => {
    const previous = lastRun.current;
    const keyChanged = previous.key !== paramsKey;
    const changed = keyChanged || previous.page !== page
                 || previous.tick !== tick || previous.userId !== userId;
    lastRun.current = { key: paramsKey, page, tick, userId };
    if (!changed) return undefined;
    // A new search / category / tab resets the page to 1 in the effect above —
    // wait for that render instead of asking the API for the old page number.
    if (keyChanged && page !== 1) return undefined;

    let alive = true;
    setLoading(true);
    loadingRef.current = true;
    cFeed({
      q: q || undefined,
      category_id: categoryId || undefined,
      sort,
      seed: sort === 'random' ? seedRef.current : undefined,
      following: scope === 'following' && userId ? 1 : undefined,
      page,
      limit: PAGE_SIZE,
    })
      .then((res) => {
        if (!alive) return;
        const items = res?.items || [];
        setPosts((prev) => (page === 1 ? items : [...prev, ...items]));
        setPages(res?.pages || 1);
        setTotal(res?.total || 0);
        setAllTotal(res?.all_total ?? res?.total ?? 0);
      })
      .catch(() => { if (alive && page === 1) setPosts([]); })
      .finally(() => { if (alive) { setLoading(false); loadingRef.current = false; } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, page, tick, userId]);

  /** Only 5 articles left below → quietly load the next 10. */
  useEffect(() => {
    const el = sentinel.current;
    if (!el || page >= pages) return undefined;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !loadingRef.current) setPage((p) => p + 1);
      });
    }, { rootMargin: '160px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [page, pages, posts.length]);

  const refresh = () => setTick((n) => n + 1);
  // A new mix starts from the first page, otherwise page 3 of the new shuffle
  // would be appended to the list the reader already has.
  const reshuffle = () => {
    seedRef.current = Math.floor(1000 + Math.random() * 900000);
    setPage(1);
    refresh();
  };
  const activeCat = cats.find((c) => String(c.id) === categoryId);
  const isTabActive = (key) => (key === 'following' ? scope === 'following' : scope === 'all' && sort === key);

  const tab = (key, label, icon) => (
    <button
      key={key}
      type="button"
      onClick={() => {
        if (key === 'following') { setScope('following'); setSort('latest'); return; }
        setScope('all');
        if (key === 'random' && sort === 'random') reshuffle();   // tap again → new mix
        setSort(key);
      }}
      className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-bold transition shrink-0 ${
        isTabActive(key) ? 'text-white' : 'text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800'
      }`}
      style={isTabActive(key) ? { background: BLUE } : undefined}
    >
      {icon} {label}
    </button>
  );

  return (
    <div className="mx-auto max-w-[1500px] px-3 lg:px-4 py-4 grid grid-cols-1 lg:grid-cols-[230px_minmax(0,1fr)_230px] xl:grid-cols-[270px_minmax(0,1fr)_270px] gap-4 xl:gap-5 items-start">
      {/* Left “General” menu built by the admin · centred feed · right rail.
          Same max-w-[1500px] container as the navbar, and the two rails are the
          same width so the middle column stays exactly centred. */}
      <CommunitySidebar>
        {/* Left rail: every shop that sells something → opens the Shop page filtered */}
        <ShopsRail />
      </CommunitySidebar>

      <div className="min-w-0 space-y-4">
        {/* First page band — CodeKhmerLearning Community (blue) with the logo */}
        <section className="community-hero rounded-2xl overflow-hidden text-white px-4 py-4 sm:px-5 sm:py-5 shadow-sm">
          <div className="flex items-center gap-3 sm:gap-4">
            <span className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white flex items-center justify-center shrink-0 shadow-md">
              <img
                src={`${process.env.PUBLIC_URL}/ckl.png`}
                alt="CodeKhmerLearning"
                className="w-11 h-11 sm:w-[52px] sm:h-[52px] object-contain"
              />
            </span>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-extrabold leading-tight">{t('communityTitle')}</h1>
              <p className="text-xs sm:text-sm text-white/90 mt-1">{t('communityTagline')}</p>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/15">
              {t('communityFreeForAll')}
            </span>
            <Link
              to="/shops"
              className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-white text-[#0B5FDB] hover:brightness-95"
            >
              🛍️ {t('startYourShop')}
            </Link>
          </div>

          <p className="mt-3 text-[11px] text-white/75">{t('communityByline')}</p>
        </section>

        <PostComposer
          startOpen={writing && !!user}
          focusKey={params.get('t') || ''}
          onSaved={() => { setPage(1); refresh(); }}
        />

        <StoriesBar />

        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
          {tab('random', t('randomFeed'), <FiShuffle className="w-4 h-4" />)}
          {tab('latest', t('latest'), <FiClock className="w-4 h-4" />)}
          {tab('popular', t('popular'), <FiTrendingUp className="w-4 h-4" />)}
          {user && tab('following', t('followingFeed'), <FiStar className="w-4 h-4" />)}
        </div>

        {(q || activeCat) && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            {q && <span>🔍 {q}</span>}
            {activeCat && (
              <span className="font-bold" style={{ color: BLUE }}>
                {activeCat.icon} {lang === 'kh' ? (activeCat.name_km || activeCat.name) : activeCat.name}
              </span>
            )}
            <span>· {total}</span>
            <Link to="/" className="ml-auto underline">{t('allCategories')}</Link>
          </div>
        )}

        {loading && page === 1 ? (
          <div className="flex items-center justify-center gap-2 py-14 text-gray-500">
            <FiLoader className="w-5 h-5 animate-spin" /> {t('loadingPosts')}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800">
            <img src={`${process.env.PUBLIC_URL}/ckl.png`} alt="CodeKhmerLearning" className="w-14 h-14 mx-auto opacity-90" />
            <p className="mt-3 font-bold">{t('noPosts')}</p>
          </div>
        ) : (
          posts.map((p, i) => (
            <React.Fragment key={p.id}>
              <div className="anim-fade-up">
                <PostCard
                  post={p}
                  onChanged={(updated) => setPosts((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)))}
                  onDeleted={(id) => setPosts((prev) => prev.filter((x) => x.id !== id))}
                />
              </div>
              {/* 5 articles before the end of what is loaded → fetch the next 10 */}
              {i === posts.length - AUTO_BEFORE && page < pages
                && <div ref={sentinel} className="h-1" aria-hidden="true" />}
            </React.Fragment>
          ))
        )}

        {/* loading the next page (auto, on scroll) */}
        {loading && page > 1 && (
          <div className="flex items-center justify-center gap-2 py-4 text-gray-500 text-sm">
            <FiLoader className="w-4 h-4 animate-spin" /> {t('loadingMore')}
          </div>
        )}

        {!loading && page >= pages && posts.length > 0 && (
          <p className="text-center text-xs text-gray-400 py-3">{t('noMorePosts')}</p>
        )}
      </div>

      {/* Sidebar (desktop) */}
      <aside className="hidden lg:block no-scrollbar space-y-4 sticky top-[72px] max-h-[calc(100vh-88px)] overflow-y-auto overscroll-contain">
        <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4">
          <h3 className="font-extrabold text-sm mb-2 flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg flex items-center justify-center text-white text-xs" style={{ background: BLUE }}>#</span>
            {t('categoryPost')}
          </h3>
          <div className="space-y-0.5">
            {/* “ទាំងអស់” — every article, including posts without a category */}
            <Link
              to="/"
              className={`flex items-center justify-between px-2.5 py-2 rounded-xl text-sm hover:bg-gray-100 dark:hover:bg-gray-800 ${
                !categoryId ? 'font-bold' : 'text-gray-700 dark:text-gray-200'
              }`}
              style={!categoryId ? { color: BLUE } : undefined}
            >
              <span>🌈 {t('allPosts')}</span>
              <span className="text-[11px] text-gray-400">{allTotal}</span>
            </Link>
            {cats.map((c) => (
              <Link
                key={c.id}
                to={`/?category_id=${c.id}`}
                className={`flex items-center justify-between px-2.5 py-2 rounded-xl text-sm hover:bg-gray-100 dark:hover:bg-gray-800 ${
                  String(c.id) === categoryId ? 'font-bold' : 'text-gray-700 dark:text-gray-200'
                }`}
                style={String(c.id) === categoryId ? { color: BLUE } : undefined}
              >
                <span>{c.icon} {lang === 'kh' ? (c.name_km || c.name) : c.name}</span>
                <span className="text-[11px] text-gray-400">{c.post_count ?? 0}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* Products of every shop + filter by product category */}
        <ProductRail />

        <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-extrabold text-sm">{t('suggestedMembers')}</h3>
            <Link to="/people" className="text-[11px] font-bold" style={{ color: BLUE }}>{t('seeAll')}</Link>
          </div>
          <div className="space-y-2">
            {people.length === 0 && <p className="text-xs text-gray-400">{t('loading')}</p>}
            {people.map((p) => (
              <div key={p.id} className="flex items-center gap-2.5">
                <UserAvatar user={p} size={36} to={`/u/${p.username}`} />
                <div className="min-w-0 flex-1">
                  <Link to={`/u/${p.username}`} className="block text-sm font-semibold truncate hover:underline">
                    {p.name || p.username}
                  </Link>
                  <span className="text-[11px] text-gray-400">@{p.username} · {p.followers_count} {t('followers')}</span>
                </div>
                <FollowButton user={p} />
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-extrabold text-sm">{t('shopsDirectory')}</h3>
            <Link to="/shops" className="text-[11px] font-bold" style={{ color: BLUE }}>{t('seeAll')}</Link>
          </div>
          <div className="space-y-2">
            {shops.map((s) => (
              <div key={s.id} className="flex items-center gap-2.5">
                {s.logo
                  ? <img src={fullUrl(s.logo)} alt={s.shop_name} className="w-9 h-9 rounded-xl object-cover" />
                  : <span className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-bold" style={{ background: BLUE }}>
                      {(s.shop_name || s.username).slice(0, 2).toUpperCase()}
                    </span>}
                <div className="min-w-0 flex-1">
                  <Link to={`/${s.username}`} className="block text-sm font-semibold truncate hover:underline">{s.shop_name}</Link>
                  <span className="text-[11px] text-gray-400">@{s.username} · {s.product_count} {t('productsCount')}</span>
                </div>
              </div>
            ))}
          </div>
          <Link
            to="/create-shop"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-bold"
            style={{ background: BLUE }}
          >
            {t('createShopTitle')}
          </Link>
        </section>
      </aside>
    </div>
  );
}
